import React from 'react';
import { Trash2 } from 'lucide-react';
import type { GuidelineDraftRule } from '../../types/accuracy';
import type { GuidelineRegularExpressionCondition } from '../../types/guideline';
import {
  applyRuleTypeChange,
  getRuleTypeLabel,
  PATROL_SOURCE_OPTIONS,
  RULE_TYPE_OPTIONS,
  shouldShowRuleField,
} from '../../services/guidelineRuleFormUtils';

export function GuidelineDraftRuleForm({
  rule,
  ruleIndex,
  onChange,
}: {
  rule: GuidelineDraftRule;
  ruleIndex: number;
  onChange: (rule: GuidelineDraftRule) => void;
}) {
  const updateRegexList = (
    field: 'forbidden_regular_expressions' | 'musthave_regular_expressions',
    values: string[]
  ) => {
    onChange({ ...rule, [field]: values });
  };

  return (
    <div className="rule-detail-content">
      <div className="rule-detail-field">
        <label>ルール名:</label>
        <input
          type="text"
          className="rule-detail-input"
          value={rule.name ?? ''}
          onChange={(e) => onChange({ ...rule, name: e.target.value })}
          placeholder="ルール名を入力"
        />
      </div>

      <div className="rule-detail-field">
        <label>タイプ:</label>
        <select
          className="rule-detail-input"
          value={rule.type || 'text_check'}
          onChange={(e) => onChange(applyRuleTypeChange(rule, e.target.value))}
        >
          {RULE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="guideline-draft-rule-type-hint">{getRuleTypeLabel(rule.type)}</span>
      </div>

      {shouldShowRuleField(rule, 'patrol_source') && (
        <div className="rule-detail-field">
          <label>パトロールソース:</label>
          <select
            className="rule-detail-input"
            value={rule.patrol_source || 'all_page'}
            onChange={(e) => onChange({ ...rule, patrol_source: e.target.value })}
          >
            {PATROL_SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {rule.type === 'forbidden_image' && (
        <GuidelineDraftImageRuleFields rule={rule} onChange={onChange} kind="forbidden" />
      )}
      {rule.type === 'required_image_match' && (
        <>
          <div className="rule-detail-field">
            <label>対象画像抽出プロンプト:</label>
            <textarea
              className="rule-detail-textarea"
              value={(rule as any).target_image_extraction_prompt ?? ''}
              onChange={(e) =>
                onChange({ ...rule, target_image_extraction_prompt: e.target.value } as GuidelineDraftRule)
              }
              rows={4}
            />
          </div>
          <GuidelineDraftImageRuleFields rule={rule} onChange={onChange} kind="required" />
        </>
      )}

      {shouldShowRuleField(rule, 'violation_check_prompt') && (
        <div className="rule-detail-field">
          <label>違反チェックプロンプト:</label>
          <textarea
            className="rule-detail-textarea"
            value={rule.violation_check_prompt ?? ''}
            onChange={(e) => onChange({ ...rule, violation_check_prompt: e.target.value })}
            rows={4}
          />
        </div>
      )}

      {shouldShowRuleField(rule, 'existence_check_prompt') && (
        <div className="rule-detail-field">
          <label>存在チェックプロンプト:</label>
          <textarea
            className="rule-detail-textarea"
            value={rule.existence_check_prompt ?? ''}
            onChange={(e) => onChange({ ...rule, existence_check_prompt: e.target.value })}
            rows={4}
          />
        </div>
      )}

      {shouldShowRuleField(rule, 'forbidden_regular_expressions') && (
        <RegexListField
          label="禁止正規表現"
          values={rule.forbidden_regular_expressions ?? []}
          onChange={(values) => updateRegexList('forbidden_regular_expressions', values)}
        />
      )}

      {shouldShowRuleField(rule, 'forbidden_detect_message') && (
        <div className="rule-detail-field">
          <label>禁止検出メッセージ:</label>
          <textarea
            className="rule-detail-textarea"
            value={rule.forbidden_detect_message ?? ''}
            onChange={(e) => onChange({ ...rule, forbidden_detect_message: e.target.value })}
            rows={3}
          />
        </div>
      )}

      {shouldShowRuleField(rule, 'musthave_regular_expressions') && (
        <RegexListField
          label="必須正規表現"
          values={rule.musthave_regular_expressions ?? []}
          onChange={(values) => updateRegexList('musthave_regular_expressions', values)}
        />
      )}

      {shouldShowRuleField(rule, 'musthave_not_detect_message') && (
        <div className="rule-detail-field">
          <label>必須未検出メッセージ:</label>
          <textarea
            className="rule-detail-textarea"
            value={rule.musthave_not_detect_message ?? ''}
            onChange={(e) => onChange({ ...rule, musthave_not_detect_message: e.target.value })}
            rows={3}
          />
        </div>
      )}

      {shouldShowRuleField(rule, 'regular_expression_conditions') && (
        <RegexConditionsField
          conditions={rule.regular_expression_conditions ?? []}
          onChange={(conditions) => onChange({ ...rule, regular_expression_conditions: conditions })}
        />
      )}

      {rule.detail != null && rule.detail !== '' && (
        <div className="rule-detail-field">
          <label>詳細 (legacy):</label>
          <textarea
            className="rule-detail-textarea"
            value={rule.detail ?? ''}
            onChange={(e) => onChange({ ...rule, detail: e.target.value })}
            rows={3}
          />
        </div>
      )}
    </div>
  );
}

function RegexListField({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const list = values.length > 0 ? values : [''];
  return (
    <div className="rule-detail-field">
      <label>{label}:</label>
      <div className="rule-detail-list">
        {list.map((expr, index) => (
          <div key={index} className="rule-detail-list-item">
            <input
              type="text"
              className="rule-detail-input-inline rule-detail-regex-input"
              value={expr}
              onChange={(e) => {
                const next = [...list];
                next[index] = e.target.value;
                onChange(next.filter((v, i) => v !== '' || i < next.length - 1));
              }}
            />
            <button
              type="button"
              className="btn-icon btn-icon-danger btn-icon-sm"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
              title="削除"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onChange([...list, ''])}
        >
          追加
        </button>
      </div>
    </div>
  );
}

function RegexConditionsField({
  conditions,
  onChange,
}: {
  conditions: NonNullable<GuidelineDraftRule['regular_expression_conditions']>;
  onChange: (conditions: NonNullable<GuidelineDraftRule['regular_expression_conditions']>) => void;
}) {
  const list: GuidelineRegularExpressionCondition[] =
    conditions.length > 0 ? conditions : [{ type: 'allow_if', rule: '', context_window: null }];
  return (
    <div className="rule-detail-field">
      <label>正規表現条件:</label>
      <div className="rule-detail-list">
        {list.map((cond, index) => (
          <div key={index} className="guideline-draft-regex-condition">
            <input
              type="text"
              className="rule-detail-input"
              value={cond.type || 'allow_if'}
              onChange={(e) => {
                const next = [...list];
                next[index] = { ...cond, type: e.target.value };
                onChange(next);
              }}
              placeholder="type"
            />
            <input
              type="number"
              className="rule-detail-input"
              value={cond.context_window ?? ''}
              onChange={(e) => {
                const next = [...list];
                next[index] = {
                  ...cond,
                  context_window: e.target.value === '' ? null : Number(e.target.value),
                };
                onChange(next);
              }}
              placeholder="context_window"
            />
            <textarea
              className="rule-detail-textarea"
              value={cond.rule ?? ''}
              onChange={(e) => {
                const next = [...list];
                next[index] = { ...cond, rule: e.target.value };
                onChange(next);
              }}
              rows={3}
              placeholder="rule"
            />
            <button
              type="button"
              className="btn-icon btn-icon-danger btn-icon-sm"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onChange([...list, { type: 'allow_if', rule: '', context_window: null }])}
        >
          条件を追加
        </button>
      </div>
    </div>
  );
}

function GuidelineDraftImageRuleFields({
  rule,
  onChange,
  kind,
}: {
  rule: GuidelineDraftRule;
  onChange: (rule: GuidelineDraftRule) => void;
  kind: 'forbidden' | 'required';
}) {
  const assetsKey = kind === 'forbidden' ? 'forbidden_image_assets' : 'required_image_assets';
  const assets = ((rule as any)[assetsKey] as any[]) || [];

  const patchNumber = (field: string, raw: string) => {
    const next: any = { ...rule };
    if (raw === '') delete next[field];
    else next[field] = Number(raw);
    onChange(next);
  };

  return (
    <div className="rule-detail-field">
      <div className="forbidden-image-card">
        <div className="forbidden-image-sectionTitle">画像一致パラメータ</div>
        <div className="forbidden-image-toolbar">
          {kind === 'forbidden' && (
            <div className="forbidden-image-thresholdRow">
              <label>similarity_threshold</label>
              <input
                type="number"
                step={0.01}
                className="rule-detail-input forbidden-image-threshold"
                value={(rule as any).similarity_threshold ?? ''}
                onChange={(e) => patchNumber('similarity_threshold', e.target.value)}
              />
            </div>
          )}
          {(['min_good_matches', 'min_score', 'min_inlier_ratio'] as const).map((field) => (
            <div key={field} className="forbidden-image-thresholdRow">
              <label>{field.replace(/_/g, ' ')}</label>
              <input
                type="number"
                step={field === 'min_good_matches' ? 1 : 0.01}
                className="rule-detail-input forbidden-image-threshold"
                value={(rule as any)[field] ?? ''}
                onChange={(e) => patchNumber(field, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="forbidden-image-divider" />
        <div className="forbidden-image-sectionTitle">画像アセット（参照のみ）</div>
        {assets.length === 0 ? (
          <p className="guideline-draft-snapshot-note">画像アセットは YAML またはガイドライン管理画面で編集してください。</p>
        ) : (
          <ul className="guideline-draft-image-asset-list">
            {assets.map((asset) => (
              <li key={asset.id}>
                {asset.original_filename || asset.file_path || asset.id}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
